import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { env } from './config/env';

// ── Primary client (uses anon key + JWT from logged-in user) ─────────────────
// RLS enforced at DB level for every operation.
// Env is parsed, validated, and environment-pinned in src/lib/config/env.ts —
// this module never reads import.meta.env directly.
const supabaseUrl     = env.supabaseUrl;
const supabaseAnonKey = env.supabaseAnonKey;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ── SECURITY NOTE ─────────────────────────────────────────────────────────────
// There is NO service-role client in the frontend.
// The Supabase service role key MUST NEVER be exposed in frontend code because:
//   1. Vite bundles all VITE_* env vars into the client-side JavaScript.
//   2. Anyone who downloads the JS bundle can read the key.
//   3. The service role key bypasses ALL Row-Level Security policies.
//
// All operations that require service-role access (admin user creation, bulk
// imports, background jobs, scheduled reports) MUST use Edge Functions, which
// run server-side and receive the key via Supabase Vault/secrets — never via
// VITE_ environment variables.
//
// If you need a privileged DB operation from the frontend, create an Edge
// Function that validates the caller's JWT, checks their role via profiles,
// then performs the operation with its own internal service-role client.
// ─────────────────────────────────────────────────────────────────────────────

// ── Analytics client (read replica for dashboards/reports) ───────────────────
// Falls back to the primary client if replica env vars are not set.
// Set VITE_SUPABASE_ANALYTICS_URL + VITE_SUPABASE_ANALYTICS_ANON_KEY when
// a read replica is provisioned to offload heavy reporting queries.
const analyticsUrl = env.analyticsUrl;
const analyticsKey = env.analyticsAnonKey;

export const supabaseAnalytics: SupabaseClient =
  analyticsUrl && analyticsKey
    ? createClient(analyticsUrl, analyticsKey, {
        auth: { autoRefreshToken: false, persistSession: false },
      })
    : supabase;
